import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import StoreButtons from "./StoreButtons";
import PhoneMockups from "./PhoneMockups";

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.5 + i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const { t, lang } = useLanguage();
  const h = t.hero;

  return (
    <section
      id="top"
      className="relative overflow-hidden pt-36 pb-16 md:pt-44 md:pb-24"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute -top-24 -left-24 h-[380px] w-[380px] rounded-full bg-[#7B1113]/6 blur-[120px]" />
      <div className="pointer-events-none absolute top-40 -right-24 h-[420px] w-[420px] rounded-full bg-[#F5A623]/12 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          key={`eyebrow-${lang}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mx-auto mb-7 inline-flex items-center gap-2 rounded-full border border-[#7B1113]/12 bg-white/60 px-4 py-1.5 backdrop-blur-xl"
          data-testid="hero-eyebrow"
        >
          <span className="h-2 w-2 rounded-full bg-[#F5A623] animate-pulse" />
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[#7B1113]">
            {h.eyebrow}
          </span>
        </motion.div>

        <h1
          key={`heading-${lang}`}
          className="mx-auto max-w-4xl text-4xl font-semibold leading-[1.08] tracking-tight text-[#2A1112] sm:text-5xl md:text-6xl lg:text-[4.75rem]"
          data-testid="hero-heading"
        >
          <span className="block overflow-hidden pb-1.5">
            <motion.span
              custom={0}
              variants={lineVariant}
              initial="hidden"
              animate="show"
              className="block"
            >
              {h.headingLine1}
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-1.5">
            <motion.span
              custom={1}
              variants={lineVariant}
              initial="hidden"
              animate="show"
              className="block"
            >
              {h.headingPre}
              <span className="italic text-[#7B1113]">{h.headingEmph}</span>
            </motion.span>
          </span>
        </h1>

        <motion.p
          key={`sub-${lang}`}
          custom={0}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-[#5E4646] md:text-lg"
          data-testid="hero-subtitle"
        >
          {h.subtitle}
          <span className="mt-2 block font-medium text-[#2A1112]">
            {h.subtitleStrong}
          </span>
        </motion.p>

        <motion.div
          custom={1}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="mt-9 flex justify-center"
        >
          <StoreButtons align="center" testidPrefix="hero" size="lg" />
        </motion.div>

        <motion.ul
          key={`badges-${lang}`}
          custom={2}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="mx-auto mt-9 flex max-w-3xl flex-wrap items-center justify-center gap-x-6 gap-y-3"
          data-testid="hero-badges"
        >
          {h.badges.map((badge, i) => (
            <li
              key={i}
              className="inline-flex items-center gap-2 text-sm font-medium text-[#5E4646]"
            >
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#7B1113]/10">
                <Check className="h-3 w-3 text-[#7B1113]" strokeWidth={3} />
              </span>
              {badge}
            </li>
          ))}
        </motion.ul>
      </div>

      <div className="relative mt-24 px-6 md:mt-36">
        <PhoneMockups />
      </div>
    </section>
  );
};

export default Hero;
